import type { ChatContextValue } from '../context/chatContextCore'
import type { Workspace } from '../context/WorkspaceContext'
import type { AnalysisViewMode } from '../types/analysis'
import type { LiveModuleRecord, LiveModuleSparkline } from '../components/Analysis/modules/LiveModuleCard'
import { resolveExportBackground, svgToPngBlob, writeExportAuditEvent } from './analysisExportService'

export const SPARKLINE_WIDTH = 200
export const SPARKLINE_HEIGHT = 60
export const MAX_CARD_IMAGE_BYTES = 100 * 1024

const toDataUrl = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onerror = () => reject(new Error('Could not prepare chart preview.'))
  reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Could not prepare chart preview.'))
  reader.readAsDataURL(blob)
})

export const captureChartSnapshotPng = async (svgElement: SVGElement | null): Promise<string> => {
  if (!svgElement) return ''
  try {
    let blob = await svgToPngBlob(svgElement, 1, resolveExportBackground().hex)
    if (blob.size > MAX_CARD_IMAGE_BYTES) {
      blob = await svgToPngBlob(svgElement, 0.5, resolveExportBackground().hex)
    }
    if (blob.size > MAX_CARD_IMAGE_BYTES) {
      return ''
    }
    return await toDataUrl(blob)
  } catch {
    return ''
  }
}

export const sendChartCard = async ({
  channelId,
  chartId,
  title,
  svgElement,
  sendMessage,
}: {
  workspace?: Workspace
  channelId: string
  chartId: string
  title: string
  svgElement?: SVGElement | null
  sendMessage: ChatContextValue['sendMessage']
}) => {
  const snapshotPngUrl = await captureChartSnapshotPng(svgElement ?? null)
  await sendMessage(title, {
    channelId,
    metadata: {
      chartCard: {
        chartId,
        title,
        snapshotPngUrl,
        sharedAt: new Date().toISOString(),
      },
    },
  })
}

export const captureSparklinePng = async ({ points, direction = 'up-is-good' }: LiveModuleSparkline): Promise<string> => {
  const safePoints = points.length > 0 ? points : [0]
  const maximum = Math.max(1, ...safePoints)
  const polyline = safePoints.map((point, index) => {
    const x = safePoints.length === 1 ? SPARKLINE_WIDTH / 2 : (index / (safePoints.length - 1)) * (SPARKLINE_WIDTH - 8) + 4
    const y = SPARKLINE_HEIGHT - (point / maximum) * (SPARKLINE_HEIGHT - 12) - 6
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', `0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`)
  svg.setAttribute('width', String(SPARKLINE_WIDTH))
  svg.setAttribute('height', String(SPARKLINE_HEIGHT))
  svg.style.cssText = `position:fixed;left:-9999px;top:0;width:${SPARKLINE_WIDTH}px;height:${SPARKLINE_HEIGHT}px;pointer-events:none;`
  svg.innerHTML = `<polyline points="${polyline}" fill="none" stroke="${direction === 'up-is-bad' ? '#fb7185' : '#34d399'}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>` // mosaic-keep: data (rendered svg sparkline export stroke)
  document.body.appendChild(svg)
  try {
    const blob = await svgToPngBlob(svg, 2, resolveExportBackground().hex)
    if (blob.size > MAX_CARD_IMAGE_BYTES) throw new Error('Chart preview exceeds the 100KB chat-card limit.')
    return await toDataUrl(blob)
  } finally {
    svg.remove()
  }
}

export const sendModuleCard = async ({
  workspace,
  channelId,
  moduleId,
  moduleTitle,
  records,
  sparkline,
  viewMode,
  snapshotId,
  sendMessage,
}: {
  workspace: Workspace
  channelId: string
  moduleId: string
  moduleTitle: string
  records: LiveModuleRecord[]
  sparkline?: LiveModuleSparkline
  viewMode: Exclude<AnalysisViewMode, 'COMPARE'>
  snapshotId?: string
  sendMessage: ChatContextValue['sendMessage']
}) => {
  const sparklinePngDataUrl = sparkline ? await captureSparklinePng(sparkline) : ''
  const deepLinkPath = `/console/workspace/${encodeURIComponent(workspace.slug)}/analysis?module=${encodeURIComponent(moduleId)}`
  await sendMessage(moduleTitle, {
    channelId,
    metadata: {
      analyticsCard: {
        moduleId,
        moduleTitle,
        sparklinePngDataUrl,
        topRecords: records.slice(0, 3).map(({ id, primary, secondary, numeric }) => ({ id, primary, ...(secondary ? { secondary } : {}), ...(typeof numeric === 'number' ? { numeric } : {}) })),
        generatedAt: new Date().toISOString(),
        deepLinkPath,
      },
    },
  })
  await writeExportAuditEvent({ workspace, format: 'CHAT_CARD', target: 'MODULE', viewMode, moduleId, moduleTitle, snapshotId, channelId })
}
